import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/utils/api";
import { fmtCurrency, fmtDate, fmt } from "@/utils/format";
import { toast } from "sonner";
import { Download, Users, Package, ShoppingCart, BarChart3, AlertTriangle } from "lucide-react";

const TABS = [
  { key: "cari", labelKey: "reports.cari_bakiye", icon: Users },
  { key: "stok", labelKey: "reports.stok_degeri", icon: Package },
  { key: "siparis", labelKey: "reports.siparis", icon: ShoppingCart },
  { key: "yaslandirma", labelKey: "reports.yaslandirma", icon: BarChart3 },
];

const BUCKET_COLORS = {
  "0-30 gün": "bg-green-100 text-green-700 border-green-200",
  "31-60 gün": "bg-amber-100 text-amber-700 border-amber-200",
  "61-90 gün": "bg-orange-100 text-orange-700 border-orange-200",
  "90+ gün": "bg-red-100 text-red-700 border-red-200",
};

export default function Raporlar() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("cari");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [data, setData] = useState([]);
  const [yaslandirma, setYaslandirma] = useState({ items: [], summary: {} });
  const [loading, setLoading] = useState(false);
  const [siparisDates, setSiparisDates] = useState({ baslangic: "", bitis: "" });
  const [downloading, setDownloading] = useState(false);

  const requestNumber = useRef(0);
  const fetchData = useCallback(async () => {
    const currentRequest = ++requestNumber.current;
    setLoading(true);
    try {
      if (tab === "cari") {
        const r = await api.get("/raporlar/cari", { params: { page } });
        if (currentRequest !== requestNumber.current) return;
        setData(r.data.items); setTotal(r.data.total);
      } else if (tab === "stok") {
        const r = await api.get("/raporlar/stok", { params: { page } });
        if (currentRequest !== requestNumber.current) return;
        setData(r.data.items); setTotal(r.data.total);
      } else if (tab === "siparis") {
        const params = { page };
        if (siparisDates.baslangic) params.baslangic = siparisDates.baslangic;
        if (siparisDates.bitis) params.bitis = siparisDates.bitis;
        const r = await api.get("/raporlar/siparisler", { params });
        if (currentRequest !== requestNumber.current) return;
        setData(r.data.items); setTotal(r.data.total);
      } else if (tab === "yaslandirma") {
        const r = await api.get("/raporlar/yaslandirma", { params: { page } });
        if (currentRequest !== requestNumber.current) return;
        setYaslandirma(r.data);
        if (currentRequest !== requestNumber.current) return;
        setData(r.data.items); setTotal(r.data.total);
      }
    } catch (e) {
      if (currentRequest === requestNumber.current) toast.error(formatApiError(e));
    } finally {
      if (currentRequest === requestNumber.current) setLoading(false);
    }
  }, [tab, siparisDates, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExcel = async () => {
    const endpoints = { cari: "/raporlar/excel/cari", stok: "/raporlar/excel/stok", siparis: "/raporlar/excel/siparisler", yaslandirma: "/raporlar/excel/yaslandirma" };
    const filenames = { cari: "cari_listesi.xlsx", stok: "stok_listesi.xlsx", siparis: "siparis_listesi.xlsx", yaslandirma: "yaslandirma.xlsx" };
    setDownloading(true);
    try {
      const r = await api.get(endpoints[tab], { responseType: "blob", params: tab === "siparis" ? Object.fromEntries(Object.entries(siparisDates).filter(([, value]) => value)) : {} });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.setAttribute("download", filenames[tab]);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel indirildi");
    } catch (e) {
      toast.error("Excel indirilemedi");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0D1B3E]" style={{ fontFamily: "Outfit, sans-serif" }}>
            {t("reports.title")}
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">{total} kayıt · Sayfa {page}</p>
        </div>
        <button onClick={handleExcel} disabled={downloading}
          className="flex items-center gap-2 border border-[#0D1B3E] text-[#0D1B3E] rounded-md px-4 py-2 text-sm font-medium hover:bg-[#0D1B3E] hover:text-white disabled:opacity-60 transition-all">
          <Download size={15} />
          {downloading ? "İndiriliyor..." : t("common.export_excel")}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex overflow-x-auto border-b border-slate-200">
        {TABS.map(({ key, labelKey, icon: Icon }) => (
          <button key={key} onClick={() => { setPage(1); setTab(key); }}
            className={`flex items-center gap-2 px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === key ? "border-[#0D1B3E] text-[#0D1B3E]" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            <Icon size={14} />{key === "yaslandirma" ? "Son Hareket Süresi" : key === "stok" ? "Stok Liste Değeri" : t(labelKey)}
          </button>
        ))}
      </div>

      <p className="text-sm text-slate-500">{tab === "yaslandirma" ? "Son cari hareketten geçen süre gösterilir; vade veya açık fatura yaşlandırması değildir. Bakiyeler bu süreye göre gruplandırılır." : tab === "stok" ? "Liste fiyatı × stok miktarıdır; alış maliyeti değildir." : tab === "siparis" ? "Sipariş tutarlarıdır; gerçekleşmiş satış veya ciro değildir." : "ERP/cari bakiyesi; siparişlerden türetilmez."}</p>
      {/* Sipariş date filter */}
      {tab === "siparis" && (
        <div className="flex items-center gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">{t("reports.start_date")}</label>
            <input type="date" value={siparisDates.baslangic}
              onChange={(e) => { setPage(1); setSiparisDates((p) => ({ ...p, baslangic: e.target.value })); }}
              className="rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D1B3E]/20" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">{t("reports.end_date")}</label>
            <input type="date" value={siparisDates.bitis}
              onChange={(e) => { setPage(1); setSiparisDates((p) => ({ ...p, bitis: e.target.value })); }}
              className="rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D1B3E]/20" />
          </div>
          <button onClick={fetchData} className="mt-5 bg-[#0D1B3E] text-white rounded-md px-4 py-2 text-sm hover:bg-[#172A58] transition-colors">Filtrele</button>
        </div>
      )}

      {/* Yaşlandırma summary */}
      {tab === "yaslandirma" && yaslandirma.summary && Object.keys(yaslandirma.summary).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(yaslandirma.summary).map(([bucket, { count, toplam }]) => (
            <div key={bucket} className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle size={13} className="text-slate-400" />
                <span className={`text-xs font-semibold px-2 py-0.5 rounded border ${BUCKET_COLORS[bucket] || "bg-slate-100 text-slate-600 border-slate-200"}`}>{bucket}</span>
              </div>
              <div className="text-lg font-bold tabular-nums text-slate-800">{fmtCurrency(toplam)}</div>
              <div className="text-xs text-slate-400">{count} cari</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 text-sm">
        <button disabled={loading || page <= 1} onClick={() => setPage(p => p - 1)} className="border rounded px-3 py-2 disabled:opacity-40">Önceki</button>
        <span>{page} / {Math.max(1, Math.ceil(total / 100))}</span>
        <button disabled={loading || page * 100 >= total} onClick={() => setPage(p => p + 1)} className="border rounded px-3 py-2 disabled:opacity-40">Sonraki</button>
        <span className="text-slate-500">Excel, seçili raporun tüm kayıtlarını içerir.</span>
      </div>
      {/* Data Table */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <div className="table-container">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-4 border-[#0D1B3E] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* Cari Report */}
              {tab === "cari" && (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                    <tr>
                      {["Kod", "Ünvan", "Telefon", "İl", "Fiyat Listesi", "Bakiye"].map((h) => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.length === 0 ? (
                      <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">{t("common.noData")}</td></tr>
                    ) : data.map((c) => (
                      <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-4 py-3 font-mono text-xs text-slate-600">{c.kod}</td>
                        <td className="px-4 py-3 font-medium text-slate-800">{c.unvan}</td>
                        <td className="px-4 py-3 text-slate-600">{c.telefon || "-"}</td>
                        <td className="px-4 py-3 text-slate-600">{c.il || "-"}</td>
                        <td className="px-4 py-3"><span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{c.fiyat_listesi || "-"}</span></td>
                        <td className={`px-4 py-3 tabular-nums font-semibold ${(c.bakiye ?? 0) > 0 ? "text-red-600" : "text-green-600"}`}>{fmtCurrency(c.bakiye ?? 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* Stok Report */}
              {tab === "stok" && (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                    <tr>
                      {["Kod", "Ad", "Marka", "Stok", "Para Birimi", "TL Liste Birim Fiyatı", "Toplam Liste Değeri"].map((h) => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.length === 0 ? (
                      <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">{t("common.noData")}</td></tr>
                    ) : data.map((s) => (
                      <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-4 py-3 font-mono text-xs text-slate-600">{s.kod}</td>
                        <td className="px-4 py-3 font-medium text-slate-800 max-w-[180px] truncate">{s.ad}</td>
                        <td className="px-4 py-3"><span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded capitalize">{s.marka}</span></td>
                        <td className="px-4 py-3 tabular-nums text-slate-700">{fmt(s.stok_miktari ?? 0, 0)} {s.birim}</td>
                        <td className="px-4 py-3 text-xs font-semibold">{s.para_birimi}</td>
                        <td className="px-4 py-3 tabular-nums text-slate-700">{fmtCurrency(s.tl_degeri)}</td>
                        <td className="px-4 py-3 tabular-nums font-bold text-slate-800">{fmtCurrency(s.toplam_stok_degeri)}</td>
                      </tr>
                    ))}
                  </tbody>
                  {data.length > 0 && (
                    <tfoot className="bg-slate-50 border-t border-slate-200">
                      <tr>
                        <td colSpan={6} className="px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider text-right">Bu Sayfanın Liste Değeri</td>
                        <td className="px-4 py-3 tabular-nums font-bold text-[#0D1B3E] text-sm">{fmtCurrency(data.reduce((s, r) => s + (r.toplam_stok_degeri || 0), 0))}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              )}

              {/* Sipariş Report */}
              {tab === "siparis" && (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                    <tr>
                      {["Sipariş No", "Tarih", "Cari", "Durum", "Toplam Tutar", "Kalem"].map((h) => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.length === 0 ? (
                      <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">{t("common.noData")}</td></tr>
                    ) : data.map((s) => (
                      <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-4 py-3 font-mono text-xs font-medium text-[#0D1B3E]">{s.siparis_no}</td>
                        <td className="px-4 py-3 tabular-nums text-xs text-slate-500">{fmtDate(s.tarih)}</td>
                        <td className="px-4 py-3 text-slate-700">{s.cari_unvan}</td>
                        <td className="px-4 py-3"><span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{s.durum}</span></td>
                        <td className="px-4 py-3 tabular-nums font-semibold">{fmtCurrency(s.toplam_tutar)}</td>
                        <td className="px-4 py-3 tabular-nums text-slate-500">{(s.satirlar || []).length}</td>
                      </tr>
                    ))}
                  </tbody>
                  {data.length > 0 && (
                    <tfoot className="bg-slate-50 border-t border-slate-200">
                      <tr>
                        <td colSpan={4} className="px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider text-right">Bu Sayfanın Sipariş Toplamı (iptaller dahil)</td>
                        <td className="px-4 py-3 tabular-nums font-bold text-[#0D1B3E] text-sm">{fmtCurrency(data.reduce((s, r) => s + (r.toplam_tutar || 0), 0))}</td>
                        <td />
                      </tr>
                    </tfoot>
                  )}
                </table>
              )}

              {/* Yaşlandırma Report */}
              {tab === "yaslandirma" && (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                    <tr>
                      {["Kod", "Ünvan", "İl", "Telefon", "Bakiye", "Gün Sayısı", "Dilim"].map((h) => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.length === 0 ? (
                      <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Pozitif bakiye bulunan cari yok</td></tr>
                    ) : data.map((c) => (
                      <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-4 py-3 font-mono text-xs text-slate-600">{c.kod}</td>
                        <td className="px-4 py-3 font-medium text-slate-800">{c.unvan}</td>
                        <td className="px-4 py-3 text-slate-500 text-xs">{c.il || "-"}</td>
                        <td className="px-4 py-3 text-slate-500 text-xs">{c.telefon || "-"}</td>
                        <td className="px-4 py-3 tabular-nums font-semibold text-red-600">{fmtCurrency(c.bakiye)}</td>
                        <td className="px-4 py-3 tabular-nums text-slate-700 font-medium">{c.age_days < 999 ? c.age_days : "?"}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${BUCKET_COLORS[c.bucket] || "bg-slate-100 text-slate-600 border-slate-200"}`}>{c.bucket}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
